import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { subscriptionAPI, authAPI } from '@/lib/api';
import { ArrowLeft, Check, Crown, Loader2, Sparkles, Image, Video, Zap, CreditCard, Package } from 'lucide-react';
import { toast } from 'sonner';

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [creditPacks, setCreditPacks] = useState([]);
  const [creditCosts, setCreditCosts] = useState({});
  const [checkingOut, setCheckingOut] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadData();
    
    // Check for success returns
    const sessionId = searchParams.get('session_id');
    const plan = searchParams.get('plan');
    const pack = searchParams.get('pack');
    const credits = searchParams.get('credits');
    
    if (sessionId && plan) {
      verifyPayment(sessionId, plan);
    }
    if (sessionId && pack && credits) {
      verifyCredits(sessionId, pack, credits);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const plansData = await subscriptionAPI.getPlans();
      setPlans(plansData.plans || []);
      setCreditPacks(plansData.credit_packs || []);
      setCreditCosts(plansData.credit_costs || {});
      
      try {
        const [statusData, userData] = await Promise.all([
          subscriptionAPI.getStatus(),
          authAPI.getMe()
        ]);
        setStatus(statusData);
        setUser(userData);
      } catch (authError) {
        console.log('User not authenticated');
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
      // Fallback data
      setPlans([
        { id: "free", name: "Free", price: 0, features: ["1 image per month", "1 video per month (5 sec)", "Community library access", "Prompt builder", "Daily challenges", "TerryLBPrompts watermark"] },
        { id: "starter", name: "Starter", price: 14.99, features: ["No watermark", "All AI Studio tools", "Up to 60 sec videos", "Buy credits as needed"] },
        { id: "pro", name: "Pro", price: 34.99, popular: true, features: ["Everything in Starter", "Priority support", "DailyDrop AI access", "Advanced AI models"] },
        { id: "business", name: "Business", price: 89.99, features: ["Everything in Pro", "API access", "Dedicated support", "Commercial license"] }
      ]);
      setCreditPacks([
        { id: "starter_pack", name: "Starter Pack", price: 5.00, credits: 40, per_credit: "$0.125" },
        { id: "pro_pack", name: "Pro Pack", price: 10.00, credits: 100, per_credit: "$0.10", popular: true },
        { id: "business_pack", name: "Business Pack", price: 25.00, credits: 300, per_credit: "$0.083" }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (sessionId, plan) => {
    try {
      const result = await subscriptionAPI.verify(sessionId, plan);
      if (result.success) {
        toast.success(`Welcome to ${result.plan_name}! Your subscription is now active.`);
        loadData();
      }
    } catch (error) {
      toast.error('Payment verification failed. Please contact support.');
    }
  };

  const verifyCredits = async (sessionId, packId, credits) => {
    try {
      const result = await subscriptionAPI.verifyCredits(sessionId, packId);
      if (result.success && result.credits_added > 0) {
        toast.success(`${result.credits_added} credits added to your account!`);
        loadData();
      } else if (result.success && result.credits_added === 0) {
        // Already processed
        loadData();
      }
    } catch (error) {
      toast.error('Credit verification failed. Please contact support.');
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.info('Please sign in to subscribe');
      navigate('/');
      return;
    }
    if (planId === 'free') {
      toast.info('You already have Free access!');
      return;
    }
    setCheckingOut(planId);
    try {
      const { checkout_url } = await subscriptionAPI.createCheckout(planId);
      window.location.href = checkout_url;
    } catch (error) {
      toast.error('Checkout failed. Please try again.');
    } finally {
      setCheckingOut(null);
    }
  };

  const handleBuyCredits = async (packId) => {
    if (!user) {
      toast.info('Please sign in to buy credits');
      navigate('/');
      return;
    }
    setCheckingOut(packId);
    try {
      const { checkout_url } = await subscriptionAPI.buyCredits(packId);
      window.location.href = checkout_url;
    } catch (error) {
      toast.error('Checkout failed. Please try again.');
    } finally {
      setCheckingOut(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Plans & Credits</h1>
            <p className="text-sm text-gray-500">Choose a plan and buy credits for AI generation</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Status */}
        {status && (
          <Card className="mb-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-purple-200 text-sm">Current Plan</p>
                  <p className="text-2xl font-bold">{status.plan_name || 'Free'}</p>
                </div>
                <div className="text-right">
                  <p className="text-purple-200 text-sm">Credits Balance</p>
                  <p className="text-3xl font-bold">{status.credits || 0}</p>
                </div>
                {status.plan === 'free' && (
                  <div className="text-right">
                    <p className="text-purple-200 text-sm">Free Monthly Usage</p>
                    <p className="text-sm">
                      Images: {status.free_images_remaining ?? 1}/1 remaining<br/>
                      Videos: {status.free_videos_remaining ?? 1}/1 remaining
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Credit Packs Section */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">Buy Credits</h2>
            <p className="text-gray-600">1 Credit ≈ 1 AI Image • Most images cost less than $0.10</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {creditPacks.map((pack) => (
              <Card 
                key={pack.id} 
                className={`relative overflow-hidden transition-all hover:shadow-lg ${pack.popular ? 'border-2 border-purple-500 shadow-purple-100' : ''}`}
              >
                {pack.popular && (
                  <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    BEST VALUE
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Package className="h-6 w-6 text-purple-600" />
                    <h3 className="text-xl font-bold">{pack.name}</h3>
                  </div>
                  <div className="mb-4">
                    <span className="text-4xl font-bold">${pack.price}</span>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 mb-4">
                    <p className="text-2xl font-bold text-purple-600">{pack.credits} Credits</p>
                    <p className="text-sm text-gray-600">{pack.per_credit} per credit</p>
                  </div>
                  <Button 
                    className={`w-full ${pack.popular ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                    onClick={() => handleBuyCredits(pack.id)}
                    disabled={checkingOut === pack.id}
                  >
                    {checkingOut === pack.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CreditCard className="h-4 w-4 mr-2" />
                    )}
                    Buy Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Credit Costs Info - Detailed Pricing Table */}
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-600" />
                Credit Pricing Guide
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Image Pricing */}
                <div>
                  <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                    <Image className="h-4 w-4" /> Image Generation
                  </h4>
                  <div className="bg-white rounded-lg p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>AI Studio Images</span>
                      <span className="font-semibold text-purple-600">1 credit</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Photo Composer</span>
                      <span className="font-semibold text-purple-600">1 credit</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Emoji Studio</span>
                      <span className="font-semibold text-purple-600">1 credit</span>
                    </div>
                  </div>
                </div>
                
                {/* Video Pricing */}
                <div>
                  <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                    <Video className="h-4 w-4" /> Video Generation
                  </h4>
                  <div className="bg-white rounded-lg p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Grok Video</span>
                      <span className="font-semibold text-purple-600">2 credits/sec</span>
                    </div>
                    <div className="flex justify-between">
                      <span>MiniMax Video</span>
                      <span className="font-semibold text-purple-600">5 credits</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Kling Video</span>
                      <span className="font-semibold text-purple-600">5 credits</span>
                    </div>
                    <div className="flex justify-between">
                      <span>LongCat Video</span>
                      <span className="font-semibold text-purple-600">10 credits/min</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Example Calculation */}
              <div className="mt-4 bg-purple-100 rounded-lg p-3">
                <p className="text-sm text-purple-800">
                  <strong>Example:</strong> With 100 credits ($10 Pro Pack), you can make:
                  <span className="block mt-1">• 100 AI images, OR</span>
                  <span className="block">• 50 seconds of Grok video, OR</span>
                  <span className="block">• 20 MiniMax/Kling videos (up to 60 sec each)</span>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Plans Section */}
        <div>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">Subscription Plans</h2>
            <p className="text-gray-600">Unlock features and remove watermarks</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden transition-all hover:shadow-lg ${plan.popular ? 'border-2 border-purple-500 shadow-purple-100' : ''} ${status?.plan === plan.id ? 'ring-2 ring-green-500' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                {status?.plan === plan.id && (
                  <Badge className="absolute top-2 left-2 bg-green-500">Current</Badge>
                )}
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold">${plan.price}</span>
                    {plan.price > 0 && <span className="text-gray-500">/mo</span>}
                  </div>
                  <ul className="space-y-2 mb-6">
                    {plan.features?.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full"
                    variant={plan.id === 'free' ? 'outline' : 'default'}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={checkingOut === plan.id || status?.plan === plan.id}
                  >
                    {checkingOut === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : status?.plan === plan.id ? (
                      'Current Plan'
                    ) : plan.id === 'free' ? (
                      'Free'
                    ) : (
                      'Subscribe'
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Watermark Notice for Free */}
        <Card className="mt-8 bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">
              <strong>Free plan includes watermark:</strong> "Made with TerryLBPrompts" will appear on generated images. 
              Upgrade to any paid plan to remove the watermark.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Subscription;
